import { claimAbbreviation } from '@bliks-io/contracts';
import { validateDepartmentRow } from './draftRowValidation';
import type { ParsedDepartment } from './excelParsers';
import { fillGapsByKey, indexByKey } from './fillGapsByKey';
import { normalizedKey } from '@/lib/draft-rows/normalizedKey';
import type { DepartmentInput } from './schemas';

const FILLED_FIELDS = ['abbreviation', 'parentAbbr', 'managerEmail'] as const;

/**
 * Merges the data-sync departments into the rows on screen. A department
 * matches an on-screen row by name only, and fills just that row's empty
 * fields: the source abbreviations are generated, and the source never holds a
 * description, so what the admin sees or entered stays. An unmatched
 * department appends under an abbreviation no on-screen row holds; parent
 * references follow the abbreviations the merge settled on. Returns the
 * current rows unchanged when nothing changed.
 */
export function mergeSetupDepartments(
  current: ParsedDepartment[],
  imported: ParsedDepartment[]
): ParsedDepartment[] {
  const nameOf = (data: DepartmentInput) => data.name;
  const indexByName = indexByKey(current, nameOf);
  const matches = imported.map((row) => indexByName.get(normalizedKey(row.data.name)));

  const taken = new Set(current.map((row) => normalizedKey(row.data.abbreviation)));
  taken.delete('');
  const settledAbbreviation = new Map<string, string>();
  imported.forEach((row, i) => {
    const onScreen = matches[i] === undefined ? '' : current[matches[i]].data.abbreviation;
    settledAbbreviation.set(
      row.data.abbreviation,
      onScreen || claimAbbreviation(row.data.abbreviation, taken)
    );
  });
  const settledParent = (parentAbbr: string | undefined) =>
    parentAbbr && (settledAbbreviation.get(parentAbbr) ?? parentAbbr);

  const settled = imported.map((row) => {
    const data = {
      ...row.data,
      abbreviation: settledAbbreviation.get(row.data.abbreviation)!,
      parentAbbr: settledParent(row.data.parentAbbr)
    };
    return { ...row, data, ...validateDepartmentRow(data) };
  });
  return fillGapsByKey(current, settled, {
    keyOf: nameOf,
    fields: FILLED_FIELDS,
    validate: validateDepartmentRow
  });
}
